import uniq from 'lodash/uniq';
import uniqWith from 'lodash/uniqWith';
import { selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { useCallback, useState } from 'react';

export const useEmailToSupplier = (
    initialData: domain.EmailToSupplier | null,
    currentUser: selectors.types.ExpandedUser,
    isDraft: boolean,
    supplierEmailSettings?: backend.SupplierEmailSettings
) => {
    const [data, setData] = useState<domain.EmailToSupplier | null>(() => {
        const initialCC = uniq([currentUser.userEmail, ...(supplierEmailSettings?.cc ?? [])]);

        if (initialData) {
            const body = initialData.body || supplierEmailSettings?.body || '';
            const subject = initialData.subject || supplierEmailSettings?.subject || '';

            return {
                ...initialData,
                cc: isDraft && initialData.cc.length === 0 ? initialCC : initialData.cc,
                body,
                subject,
                from: initialData.from || currentUser.displayName || '',
                replyTo: initialData.replyTo || currentUser.userEmail || '',
            };
        }

        return {
            from: currentUser.displayName,
            cc: initialCC,
            body: supplierEmailSettings?.body || '',
            subject: supplierEmailSettings?.subject || '',
            replyTo: currentUser.userEmail || '',
            to: [],
            attachments: [],
        };
    });

    const setEmailToSupplierData = useCallback(
        (value: domain.EmailToSupplier[keyof domain.EmailToSupplier], key: keyof domain.EmailToSupplier) => {
            setData((current) => {
                if (current) {
                    return {
                        ...current,
                        [key]: value,
                    };
                }

                return null;
            });
        },
        []
    );

    const setAttachments = useCallback((attachments: domain.RequestAttachment[]) => {
        setData((current) => {
            if (current) {
                return {
                    ...current,
                    attachments: uniqWith([...current.attachments, ...attachments], (a, b) => a.id === b.id),
                };
            }

            return null;
        });
    }, []);

    const removeAttachment = useCallback((attachmentId: string) => {
        setData((current) => {
            if (current) {
                return {
                    ...current,
                    attachments: current.attachments.filter((attachment) => attachment.id !== attachmentId),
                };
            }

            return null;
        });
    }, []);

    return {
        data,
        setEmailToSupplierData,
        setAttachments,
        removeAttachment,
    };
};
